import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import {
  Newspaper,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Flame,
  Award
} from 'lucide-react'

interface Source {
  id: number
  name: string
  domain: string | null
  type: string
  category: string
  authority: number
  totalMentions: number
  mentionsWithBrand: number
  mentionsWithCompetitors: number
  collaborationScore: number
  lastSeen: string
  badge: 'HIGH_OPPORTUNITY' | 'OPPORTUNITY' | 'ACTIVE_PARTNER' | 'INFLUENCER' | 'NEUTRAL'
}

interface SourcesData {
  sources: Source[]
  summary: {
    totalSources: number
    highOpportunities: number
    activePartners: number
    totalMentions: number
  }
}

// Demo data
const demoSources: Source[] = [
  {
    id: 1,
    name: 'TechCrunch',
    domain: 'techcrunch.com',
    type: 'PUBLICATION',
    category: 'Technology',
    authority: 85,
    totalMentions: 45,
    mentionsWithBrand: 0,
    mentionsWithCompetitors: 32,
    collaborationScore: 92,
    lastSeen: new Date().toISOString(),
    badge: 'HIGH_OPPORTUNITY'
  },
  {
    id: 2,
    name: 'Forbes',
    domain: 'forbes.com',
    type: 'PUBLICATION',
    category: 'Business',
    authority: 90,
    totalMentions: 38,
    mentionsWithBrand: 12,
    mentionsWithCompetitors: 18,
    collaborationScore: 75,
    lastSeen: new Date().toISOString(),
    badge: 'ACTIVE_PARTNER'
  },
  {
    id: 3,
    name: 'G2',
    domain: 'g2.com',
    type: 'WEBSITE',
    category: 'Reviews',
    authority: 80,
    totalMentions: 28,
    mentionsWithBrand: 15,
    mentionsWithCompetitors: 22,
    collaborationScore: 65,
    lastSeen: new Date().toISOString(),
    badge: 'ACTIVE_PARTNER'
  },
  {
    id: 4,
    name: 'Harvard Business Review',
    domain: 'hbr.org',
    type: 'PUBLICATION',
    category: 'Business',
    authority: 95,
    totalMentions: 22,
    mentionsWithBrand: 0,
    mentionsWithCompetitors: 15,
    collaborationScore: 88,
    lastSeen: new Date().toISOString(),
    badge: 'HIGH_OPPORTUNITY'
  },
  {
    id: 5,
    name: 'Gartner',
    domain: 'gartner.com',
    type: 'RESEARCH',
    category: 'Research',
    authority: 95,
    totalMentions: 18,
    mentionsWithBrand: 0,
    mentionsWithCompetitors: 0,
    collaborationScore: 50,
    lastSeen: new Date().toISOString(),
    badge: 'INFLUENCER'
  }
]

export default function AuthoritySources() {
  const { activeBrand } = useAuth()
  const [data, setData] = useState<SourcesData | null>(null)
  const [loading, setLoading] = useState(true)
  const { isDemoMode } = useDemoMode()

  useEffect(() => {
    if (!activeBrand?.userBrandId) return

    const fetchSources = async () => {
      try {
        setLoading(true)

        // In demo mode, always show demo data
        if (isDemoMode) {
          setData({
            sources: demoSources,
            summary: {
              totalSources: demoSources.length,
              highOpportunities: demoSources.filter(s => s.badge === 'HIGH_OPPORTUNITY' || s.badge === 'OPPORTUNITY').length,
              activePartners: demoSources.filter(s => s.badge === 'ACTIVE_PARTNER').length,
              totalMentions: demoSources.reduce((sum, s) => sum + s.totalMentions, 0)
            }
          })
          setLoading(false)
          return
        }

        // Not in demo mode - fetch real data
        const response = await api.get(`/intelligence/sources?userBrandId=${activeBrand.userBrandId}&limit=10`)

        // Show real data (even if empty)
        setData(response.data && response.data.sources.length > 0 ? response.data : null)

      } catch (error) {
        console.error('Error fetching sources:', error)
        // On error, show null (no sources message) instead of demo data
        setData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchSources()
  }, [activeBrand?.userBrandId, isDemoMode])

  const getBadgeDisplay = (badge: Source['badge']) => {
    switch (badge) {
      case 'HIGH_OPPORTUNITY':
        return { icon: Flame, text: 'High Opportunity', color: 'text-orange-600 bg-orange-50 border-orange-200' }
      case 'OPPORTUNITY':
        return { icon: TrendingUp, text: 'Opportunity', color: 'text-blue-600 bg-blue-50 border-blue-200' }
      case 'ACTIVE_PARTNER':
        return { icon: CheckCircle2, text: 'Active Mentions', color: 'text-green-600 bg-green-50 border-green-200' }
      case 'INFLUENCER':
        return { icon: Award, text: 'Influencer', color: 'text-purple-600 bg-purple-50 border-purple-200' }
      default:
        return { icon: AlertCircle, text: 'Neutral', color: 'text-gray-600 bg-gray-50 border-gray-200' }
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Authority Sources</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    )
  }

  if (!data || data.sources.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Authority Sources</h3>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-2">
                No sources discovered yet
              </p>
              <p className="text-sm text-blue-700 mb-3">
                Run search queries to discover which publications and sources AI providers cite when discussing your industry.
              </p>
              <div className="text-sm text-blue-700">
                <strong>How it works:</strong>
                <ol className="list-decimal list-inside mt-1 space-y-1 ml-2">
                  <li>Go to Search Queries and analyze a query</li>
                  <li>AI providers respond with their recommendations</li>
                  <li>Claude extracts all cited sources automatically</li>
                  <li>Sources appear here with citation metrics</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Authority Sources</h3>
          </div>
          <div className="text-sm text-gray-500">
            {data.summary.totalSources} sources • {data.summary.totalMentions} citations
          </div>
        </div>
        <p className="text-sm text-gray-600">
          These are the publications, websites, and platforms that AI providers actually reference when answering queries about your industry. Sources marked as 'Opportunity' frequently cite your competitors but not your brand—making them prime targets for PR outreach, guest content, or partnerships to increase your AI visibility.
        </p>
      </div>

      {/* Summary Stats
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-900">High Opportunities</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">{data.summary.highOpportunities}</p>
          <p className="text-xs text-orange-700 mt-1">
            Mention competitors but not you
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-900">Active Partners</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{data.summary.activePartners}</p>
          <p className="text-xs text-green-700 mt-1">
            Already mentioning you
          </p>
        </div>
      </div>
      */}

      {/* Sources Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Source
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Category
              </th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Citations
              </th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                % Brand
              </th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                % Competitor
              </th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.sources.map((source) => {
              const badgeInfo = getBadgeDisplay(source.badge)
              const BadgeIcon = badgeInfo.icon

              // Calculate percentages
              const brandPercentage = source.totalMentions > 0
                ? Math.round((source.mentionsWithBrand / source.totalMentions) * 100)
                : 0
              const competitorPercentage = source.totalMentions > 0
                ? Math.round((source.mentionsWithCompetitors / source.totalMentions) * 100)
                : 0

              return (
                <tr key={source.id} className="hover:bg-purple-50/30 transition-colors">
                  {/* Source Name */}
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{source.name}</span>
                          {source.domain && (
                            <a
                              href={`https://${source.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-purple-600 transition-colors"
                              title={`Visit ${source.domain}`}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        {source.domain && (
                          <div className="text-xs text-gray-500 mt-0.5">{source.domain}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                      {source.category}
                    </span>
                  </td>

                  {/* Total Citations */}
                  <td className="py-3 px-4 text-center">
                    <span className="text-sm font-semibold text-gray-900">{source.totalMentions}</span>
                  </td>

                  {/* % Brand Mention */}
                  <td className="py-3 px-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className={`text-sm font-semibold ${brandPercentage > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {brandPercentage}%
                      </span>
                      <span className="text-xs text-gray-500">({source.mentionsWithBrand})</span>
                    </div>
                  </td>

                  {/* % Competitor Mention */}
                  <td className="py-3 px-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className={`text-sm font-semibold ${competitorPercentage > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                        {competitorPercentage}%
                      </span>
                      <span className="text-xs text-gray-500">({source.mentionsWithCompetitors})</span>
                    </div>
                  </td>

                  {/* Status Badge */}
                  <td className="py-3 px-4">
                    <div className="flex justify-center">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium ${badgeInfo.color}`}>
                        <BadgeIcon className="w-3 h-3" />
                        <span className="hidden sm:inline">{badgeInfo.text}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* View All Link (Future Enhancement) */}
      {data.summary.totalSources > data.sources.length && (
        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <button className="text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors">
            View All {data.summary.totalSources} Sources →
          </button>
        </div>
      )}
    </div>
  )
}
